/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { Button } from "@/components/ui/button";
import { isWaitlistMode, openWaitlist } from "@/lib/utils/waitlist-mode";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import { type MouseEvent, useCallback } from "react";
import { FadeIn } from "./primitives/fade-in";

interface MarketingCTASectionProps {
  title: string;
  description: string;
  primaryCTA: {
    text: string;
    href: string;
  };
  secondaryCTA?: {
    text: string;
    href: string;
  };
  darkBackground?: boolean;
  trustIndicators?: string[];
}

/**
 * Shared CTA section for marketing pages
 * Can use light or dark background
 */
export function MarketingCTASection({
  title,
  description,
  primaryCTA,
  secondaryCTA,
  darkBackground = false,
  trustIndicators,
}: MarketingCTASectionProps) {
  const waitlistActive =
    isWaitlistMode() && primaryCTA.href.startsWith("/auth");

  const handlePrimaryClick = useCallback(
    (e: MouseEvent) => {
      if (waitlistActive) {
        e.preventDefault();
        openWaitlist();
      }
    },
    [waitlistActive],
  );

  if (darkBackground) {
    return (
      <section
        className="relative py-16 md:py-24 lg:py-32 bg-primary text-primary-foreground grain-texture"
        aria-label="Call to action"
      >
        {/* Blueprint grid pattern */}
        <div
          className="blueprint-grid-gold absolute inset-0 opacity-10"
          aria-hidden="true"
        />

        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
          <FadeIn className="max-w-4xl mx-auto text-center">
            <h2 className="text-[clamp(36px,5vw,64px)] leading-[1.1] font-bold font-display mb-4">
              {title}
            </h2>

            <p className="text-base text-primary-foreground/70 mb-8 leading-relaxed">
              {description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <div className="hover:scale-[1.03] active:scale-[0.97] transition-transform">
                <Button
                  asChild
                  size="pill"
                  className="bg-secondary hover:bg-secondary/90 text-white font-medium shadow-lg hover:shadow-xl transition-standard hover-lift"
                >
                  <Link href={primaryCTA.href} onClick={handlePrimaryClick}>
                    {waitlistActive ? "Join the Waitlist" : primaryCTA.text}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>

              {secondaryCTA && (
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border border-white/30 bg-white/10 hover:bg-white/20 text-primary-foreground font-medium transition-standard"
                >
                  <Link href={secondaryCTA.href}>{secondaryCTA.text}</Link>
                </Button>
              )}
            </div>

            {trustIndicators && (
              <div className="text-sm text-primary-foreground/70 space-y-2">
                {trustIndicators.map((indicator, i) => (
                  <div key={i}>{indicator}</div>
                ))}
              </div>
            )}
          </FadeIn>
        </div>
      </section>
    );
  }

  // Light background version
  return (
    <section
      className="py-16 md:py-24 lg:py-32 bg-muted/30"
      aria-label="Call to action"
    >
      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20">
        <FadeIn className="max-w-4xl mx-auto text-center">
          <h2 className="text-[clamp(36px,5vw,64px)] leading-[1.1] font-bold font-display text-foreground mb-4">
            {title}
          </h2>

          <p className="text-base text-muted-foreground mb-8 leading-relaxed">
            {description}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="pill"
              className="bg-secondary hover:bg-secondary/90 text-white font-medium shadow-lg hover:shadow-xl transition-standard hover-lift"
            >
              <Link href={primaryCTA.href} onClick={handlePrimaryClick}>
                {waitlistActive ? "Join the Waitlist" : primaryCTA.text}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>

            {secondaryCTA && (
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border border-border bg-background hover:bg-muted/50 font-medium transition-standard"
              >
                <Link href={secondaryCTA.href}>{secondaryCTA.text}</Link>
              </Button>
            )}
          </div>
        </FadeIn>
      </div>
    </section>
  );
}
